import * as React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { TextField, CircularProgress, InputAdornment } from '@material-ui/core';
import Error from '@material-ui/icons/Error';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';
import { ApiHolder } from '@backstage/core-plugin-api';

// Validation function for form-level validation (blocks navigation)
export const validateAppName = async (
  value: string,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder },
) => {
  // Ensure we're working with the current value
  const currentValue = value || '';
  
  if (!currentValue) return; // Let required validation handle empty values

  const minLength = 3;
  const maxLength = 63;
  const pattern = '^[A-Za-z0-9-]+$';

  // Basic validations - return early after first error
  if (currentValue.length < minLength) {
    validation.addError(`App name must be at least ${minLength} characters long`);
    return;
  }

  if (currentValue.length > maxLength) {
    validation.addError(`App name must be less than ${maxLength} characters`);
    return;
  }

  if (!new RegExp(pattern).test(currentValue)) {
    validation.addError('App name must be 3-63 characters long and contain only letters, numbers, and hyphens');
    return;
  }

  // Duplicate check - only run if basic validations pass
  try {
    const catalogApi = context.apiHolder.get(catalogApiRef);
    if (!catalogApi) {
      validation.addError('Unable to validate component name. Please try again.');
      return;
    }
    
    const entities = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'metadata.name': currentValue.toLowerCase(),
      },
    });

    if (entities.items.length > 0) {
      validation.addError(`A component with name "${currentValue}" already exists in the catalog`);
    }
  } catch (error) {
    validation.addError('Unable to validate component name. Please try again.');
  }
};

export const CustomAppNameField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
}: FieldExtensionComponentProps<string>) => {
  const [validationState, setValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationError, setValidationError] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState(formData || '');
  const lastValueRef = useRef(formData || '');
  const catalogApi = useApi(catalogApiRef);

  // Extract schema properties
  const {
    title,
    description,
    pattern,
    minLength,
    maxLength,
    placeholder,
    helperText,
    errorMessage,
  } = schema;

  // Debounce effect to prevent duplicate validation requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(formData || '');
    }, 500);

    return () => clearTimeout(timer);
  }, [formData]);

  // Clear validation state when input changes
  useEffect(() => {
    if (formData !== debouncedValue) {
      setValidationState('idle');
      setValidationError('');
    }
  }, [formData, debouncedValue]);

  // Check for form validation errors (these block navigation)
  const hasFormValidationErrors = rawErrors && rawErrors.length > 0;

  // Handle input change and try to clear form errors
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    // If the value has changed and there were form validation errors, try to trigger re-validation
    if (newValue !== lastValueRef.current && hasFormValidationErrors) {
      // Force a re-validation by calling onChange with a slight delay
      setTimeout(() => {
        onChange(newValue);
      }, 10);
    }
    
    lastValueRef.current = newValue;
    onChange(newValue);
  };

  // Real-time validation for UI feedback (visual only) - only run when no form errors
  const validateName = useMemo(() => async (name: string) => {
    // Don't run field validation if form validation errors are present
    if (hasFormValidationErrors) {
      return;
    }

    if (!name || name.length < 2) {
      setValidationState('idle');
      setValidationError('');
      return;
    }

    // Basic validation first
    if (minLength && name.length < minLength) {
      setValidationState('invalid');
      setValidationError(`Name must be at least ${minLength} characters long`);
      return;
    }

    if (maxLength && name.length > maxLength) {
      setValidationState('invalid');
      setValidationError(`Name must be less than ${maxLength} characters`);
      return;
    }

    if (pattern && !new RegExp(pattern).test(name)) {
      setValidationState('invalid');
      setValidationError(errorMessage || 'Name contains invalid characters');
      return;
    }

    setValidationState('validating');
    try {
      const entities = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'metadata.name': name.toLowerCase(),
        },
      });

      if (entities.items.length > 0) {
        setValidationState('invalid');
        setValidationError(`A component with name "${name}" already exists in the catalog`);
      } else {
        setValidationState('valid');
        setValidationError('');
      }
    } catch (error) {
      setValidationState('invalid');
      setValidationError('Unable to validate component name');
    }
  }, [catalogApi, minLength, maxLength, pattern, errorMessage, hasFormValidationErrors]);

  useEffect(() => {
    validateName(debouncedValue || '');
  }, [debouncedValue, validateName]);

  const getEndAdornment = () => {
    // If form validation errors exist, don't show any field-level icons
    if (hasFormValidationErrors) {
      return <div />;
    }
    
    switch (validationState) {
      case 'validating':
        return <CircularProgress size={20} />;
      case 'valid':
        return <div />; // No icon for valid state
      case 'invalid':
        return <Error style={{ color: 'red' }} />;
      default:
        return <div />;
    }
  };

  // Only show field-level errors when there are no form validation errors
  const hasError = hasFormValidationErrors || (!hasFormValidationErrors && validationState === 'invalid');
  const displayError = hasFormValidationErrors ? '' : (validationError || helperText || description);

  return (
    <TextField
      fullWidth
      label={title}
      value={formData || ''}
      onChange={handleInputChange}
      error={hasError}
      helperText={displayError}
      placeholder={placeholder}
      required={required}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {getEndAdornment()}
          </InputAdornment>
        ),
      }}
    />
  );
};